import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { functions } from '../functions/_functions';
import { withTheme } from '../core/theming';
import { PropTypesRepo } from '../functions/_propTypes';

import Div from './Div';
import Icon from './Icon';

/* eslint-disable react/prop-types */

const CustomTag = React.forwardRef((props, ref) => {
	const {
		d,
		m,
		w,
		maxW,
		minW,
		flexGrow,
		order,
		p,
		rounded,
		pos,
		border,
		borderColor,
		h,
		maxH,
		minH,
		cursor,
		bg,
		textColor,
		textWeight,
		textAlign,
		shadow,
		transition,
		textSize,
		fontFamily,
		hoverBg,
		hoverBorder,
		hoverBorderColor,
		hoverShadow,
		hoverTextColor,
		focusBg,
		focusBorder,
		focusBorderColor,
		focusShadow,
		focusTextColor,
		tag,
		children,
		...rest
	} = props;
	return React.createElement(`${tag}`, { ...rest, ref }, children);
});

/* eslint-enable react/prop-types */

const BaseInput = styled(CustomTag)`
	box-sizing: border-box;
	${(props) => props.flexGrow && functions.makeResponsive('flex-grow', props.flexGrow)};
	${(props) => props.p && functions.findDirection('padding', props.p)};
	${(props) => props.rounded && functions.findRoundedDirection(props.rounded, props.theme.rounded)};
	${(props) => props.border && functions.findDirection('border', props.border)};
	${(props) => props.pos && functions.makeResponsive('position', props.pos)};

	${(props) => props.borderColor && functions.makeResponsive('border-color', props.theme.colors[props.borderColor])};

	${(props) => props.h && functions.makeResponsive('height', props.h)};
	${(props) => props.maxH && functions.makeResponsive('max-height', props.maxH)};
	${(props) => props.minH && functions.makeResponsive('min-height', props.minH)};
	${(props) => props.w && functions.makeResponsive('width', props.w)};
	${(props) => props.maxW && functions.makeResponsive('max-width', props.maxW)};
	${(props) => props.minW && functions.makeResponsive('min-width', props.minW)};
	${(props) => props.cursor && functions.makeResponsive('cursor', props.cursor)};
	${(props) => props.bg && functions.makeResponsive('background', props.theme.colors[props.bg])};
	${(props) => props.textColor && functions.makeResponsive('color', props.theme.colors[props.textColor])};
	${(props) => props.textWeight && functions.makeResponsive('font-weight', props.textWeight)};
	${(props) => props.textAlign && functions.makeResponsive('text-align', props.textAlign)};
	${(props) => props.fontFamily && `font-family: ${props.theme.fontFamily[props.fontFamily]}`};
	${(props) => props.shadow && `box-shadow: ${props.theme.shadows[props.shadow]}`};
	${(props) => props.transition && `transition: ${props.theme.transition}`};
	${(props) => `
    font-size: ${props.theme.textSize.size[props.textSize]};
    line-height: ${props.theme.textSize.height[props.textSize]};
  `};

	:hover {
		${(props) => props.hoverBg && functions.makeResponsive('background', props.theme.colors[props.hoverBg])};
		${(props) => props.hoverTextColor && functions.makeResponsive('color', props.theme.colors[props.hoverTextColor])};
		${(props) => props.hoverBorder && functions.findDirection('border', props.hoverBorder)};
		${(props) =>
			props.hoverBorderColor && functions.makeResponsive('border-color', props.theme.colors[props.hoverBorderColor])};
		${(props) => props.hoverShadow && `box-shadow: ${props.theme.shadows[props.hoverShadow]}`};
	}

	:focus {
		outline: none;
		${(props) => props.focusBg && functions.makeResponsive('background', props.theme.colors[props.focusBg])};
		${(props) => props.focusTextColor && functions.makeResponsive('color', props.theme.colors[props.focusTextColor])};
		${(props) => props.focusBorder && functions.findDirection('border', props.focusBorder)};
		${(props) =>
			props.focusBorderColor && functions.makeResponsive('border-color', props.theme.colors[props.focusBorderColor])};
		${(props) => props.focusShadow && `box-shadow: ${props.theme.shadows[props.focusShadow]}`};
	}

	::placeholder {
		${(props) =>
			props.placeholderTextColor
				? functions.makeResponsive('color', props.theme.colors[props.placeholderTextColor])
				: `color: ${props.theme.colors.light}`};
	}

	:disabled {
		color: ${(props) => props.theme.colors.medium};
		cursor: not-allowed;
		background: ${(props) => props.theme.colors.gray200};
	}
`;

const Input = React.forwardRef(
	(
		{ children, prefix, isLoading, suffix, order, flexGrow, loadingIcon, d, m, w, maxW, minW, h, minH, maxH, ...rest },
		ref
	) => (
		<Div
			pos="relative"
			d={d}
			m={m}
			w={w}
			maxW={maxW}
			minW={minW}
			h={h}
			minH={minH}
			maxH={maxH}
			order={order}
			flexGrow={flexGrow}
		>
			{prefix}
			<BaseInput ref={ref} w="100%" h="100%" {...rest} />
			{suffix}
			{isLoading && loadingIcon}
		</Div>
	)
);

/* eslint-disable react/require-default-props */

Input.propTypes = {
	children: PropTypes.node,
	// mostly Icon
	prefix: PropTypes.node,
	isLoading: PropTypes.bool,
	loadingIcon: PropTypes.node,
	// mostly Icon
	suffix: PropTypes.node,
	flexGrow: PropTypesRepo.flexGrow,
	order: PropTypesRepo.order,
	tag: PropTypes.string,
	pos: PropTypesRepo.pos,
	d: PropTypesRepo.d,
	p: PropTypesRepo.p,
	m: PropTypesRepo.m,
	rounded: PropTypesRepo.rounded,
	border: PropTypesRepo.border,
	borderColor: PropTypesRepo.borderColor,
	h: PropTypesRepo.h,
	w: PropTypesRepo.w,
	maxH: PropTypesRepo.maxH,
	minW: PropTypesRepo.minW,
	minH: PropTypesRepo.minH,
	maxW: PropTypesRepo.maxW,
	cursor: PropTypesRepo.cursor,
	bg: PropTypesRepo.bg,
	textColor: PropTypesRepo.textColor,
	textWeight: PropTypesRepo.textWeight,
	textAlign: PropTypesRepo.textAlign,
	textSize: PropTypesRepo.textSize,
	fontFamily: PropTypesRepo.fontFamily,
	shadow: PropTypesRepo.shadow,
	transition: PropTypesRepo.transition,
	hoverBg: PropTypesRepo.hoverBg,
	hoverTextColor: PropTypesRepo.hoverTextColor,
	hoverBorderColor: PropTypesRepo.hoverBorderColor,
	hoverShadow: PropTypesRepo.hoverShadow,
	focusBorderColor: PropTypesRepo.focusBorderColor,
};

/* eslint-enable react/require-default-props */

Input.defaultProps = {
	tag: 'input',
	d: 'flex',
	p: { x: '12px' },
	m: {},
	textSize: 'body',
	rounded: 'md',
	border: '1px solid',
	borderColor: 'gray500',
	h: '35.2px',
	w: '100%',
	bg: 'white',
	textColor: 'dark',
	textWeight: '500',
	focusBorderColor: 'gray700',
	transition: true,
	loadingIcon: <Icon name="Loading" pos="absolute" top="12px" right="12px" size="16px" />,
};

export default withTheme(Input);
